/**
 * Tags — shared TanStack Query hooks for the shop tag dictionary and
 * per-entity tag attach/detach.
 *
 * The dictionary lives at `GET /api/tags`. Per-entity attach lives at
 * `POST /api/customers/:id/tags` and `POST /api/vehicles/:id/tags`.
 * Detach mirrors with DELETE.
 *
 * Cache shape:
 *   ['shop-tags']                          - shop dictionary
 *   ['customer', id]                - customer detail (already exists)
 *   ['vehicle', id]                 - vehicle detail (already exists)
 *   ['customers']                          - customer list (already exists)
 *   ['vehicles']                           - vehicle list (already exists)
 *   ['appointments']                       - appt tiles + Edit dialog chips
 *
 * Attach/detach/update/delete mutations invalidate the relevant detail +
 * list keys (incl. ['appointments'], which renders the same chips) so
 * every surface re-renders with fresh `tag_chips`.
 */

import { useAuth } from '@clerk/clerk-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import type { TagColor } from './tag-colors'

export type Tag = {
  id: string
  name: string
  color: TagColor | string
}

export type TagWithUsage = Tag & {
  customer_count: number
  vehicle_count: number
  created_at?: string | null
}

async function authedFetch(
  getToken: () => Promise<string | null>,
  url: string,
  init?: RequestInit,
): Promise<Response> {
  const token = await getToken()
  if (!token) throw new Error('missing_token')
  return fetch(url, {
    ...init,
    headers: {
      ...(init?.headers ?? {}),
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  })
}

// ─────────────────────────────────────────────────────────────────────
// Shop dictionary

export function useShopTags(options?: { enabled?: boolean }) {
  const { getToken } = useAuth()
  return useQuery({
    queryKey: ['shop-tags'],
    enabled: options?.enabled ?? true,
    queryFn: async (): Promise<TagWithUsage[]> => {
      const res = await authedFetch(getToken, '/api/tags')
      if (!res.ok) throw new Error('failed_to_load')
      const data = (await res.json()) as { tags: TagWithUsage[] }
      return data.tags ?? []
    },
  })
}

// ─────────────────────────────────────────────────────────────────────
// Dictionary mutations (owner-only at the route layer; UI hides for
// non-owners via useCanManageTags()).

export function useCreateTag() {
  const { getToken } = useAuth()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: { name: string; color?: string }) => {
      const res = await authedFetch(getToken, '/api/tags', {
        method: 'POST',
        body: JSON.stringify(input),
      })
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as
          | { error?: string }
          | null
        throw new Error(body?.error ?? `http_${res.status}`)
      }
      return (await res.json()) as Tag
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['shop-tags'] })
    },
  })
}

export function useUpdateTag() {
  const { getToken } = useAuth()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: {
      id: string
      name?: string
      color?: string
    }) => {
      const { id, ...body } = input
      const res = await authedFetch(getToken, `/api/tags/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      })
      if (!res.ok) {
        const errBody = (await res.json().catch(() => null)) as
          | { error?: string }
          | null
        throw new Error(errBody?.error ?? `http_${res.status}`)
      }
      return (await res.json()) as Tag
    },
    onSuccess: () => {
      // Rename / recolor can change any record's display anywhere, so
      // invalidate everything that renders chips. Cheap on a small DB.
      void qc.invalidateQueries({ queryKey: ['shop-tags'] })
      void qc.invalidateQueries({ queryKey: ['customers'] })
      void qc.invalidateQueries({ queryKey: ['vehicles'] })
      void qc.invalidateQueries({ queryKey: ['customer'] })
      void qc.invalidateQueries({ queryKey: ['vehicle'] })
      void qc.invalidateQueries({ queryKey: ['appointments'] })
    },
  })
}

export function useDeleteTag() {
  const { getToken } = useAuth()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await authedFetch(getToken, `/api/tags/${id}`, {
        method: 'DELETE',
      })
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as
          | { error?: string }
          | null
        throw new Error(body?.error ?? `http_${res.status}`)
      }
      return (await res.json()) as {
        ok: true
        detached_customer_count: number
        detached_vehicle_count: number
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['shop-tags'] })
      void qc.invalidateQueries({ queryKey: ['customers'] })
      void qc.invalidateQueries({ queryKey: ['vehicles'] })
      void qc.invalidateQueries({ queryKey: ['customer'] })
      void qc.invalidateQueries({ queryKey: ['vehicle'] })
      void qc.invalidateQueries({ queryKey: ['appointments'] })
    },
  })
}

// ─────────────────────────────────────────────────────────────────────
// Per-entity attach / detach.

type EntityType = 'customer' | 'vehicle'

function attachUrl(type: EntityType, id: string) {
  return type === 'customer'
    ? `/api/customers/${id}/tags`
    : `/api/vehicles/${id}/tags`
}
function detachUrl(type: EntityType, entityId: string, tagId: string) {
  return type === 'customer'
    ? `/api/customers/${entityId}/tags/${tagId}`
    : `/api/vehicles/${entityId}/tags/${tagId}`
}

function invalidateForEntity(
  qc: ReturnType<typeof useQueryClient>,
  type: EntityType,
  id: string,
) {
  // Detail keys for the specific entity, and the list key for whichever
  // surface they show on. Dictionary itself doesn't change on attach;
  // unless the body asked to create a new entry, in which case the
  // 201 response carries `created: true` (worth a future
  // micro-optimization to only invalidate dict then).
  void qc.invalidateQueries({ queryKey: ['shop-tags'] })
  if (type === 'customer') {
    void qc.invalidateQueries({ queryKey: ['customer', id] })
    void qc.invalidateQueries({ queryKey: ['customers'] })
  } else {
    void qc.invalidateQueries({ queryKey: ['vehicle', id] })
    void qc.invalidateQueries({ queryKey: ['vehicles'] })
  }
  // Appointment tiles + Edit dialog render the same chips off the
  // ['appointments'] query — refresh after attach/detach too.
  void qc.invalidateQueries({ queryKey: ['appointments'] })
}

export function useAttachTag(type: EntityType, entityId: string) {
  const { getToken } = useAuth()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (
      input: { tag_id: string } | { name: string; color?: string },
    ) => {
      const res = await authedFetch(getToken, attachUrl(type, entityId), {
        method: 'POST',
        body: JSON.stringify(input),
      })
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as
          | { error?: string }
          | null
        throw new Error(body?.error ?? `http_${res.status}`)
      }
      return (await res.json()) as { tag: Tag; created: boolean }
    },
    onSuccess: () => invalidateForEntity(qc, type, entityId),
  })
}

export function useDetachTag(type: EntityType, entityId: string) {
  const { getToken } = useAuth()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (tagId: string) => {
      const res = await authedFetch(getToken, detachUrl(type, entityId, tagId), {
        method: 'DELETE',
      })
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as
          | { error?: string }
          | null
        throw new Error(body?.error ?? `http_${res.status}`)
      }
      return (await res.json()) as { ok: true }
    },
    onSuccess: () => invalidateForEntity(qc, type, entityId),
  })
}
